import React from 'react'
import FloCheckbox from '../common/form-elements/FloCheckbox'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useRtoSuiteContext } from 'src/lib/contexts/RTOSuiteProvider'
import InputTextFieldV2 from '../common/form-elements/NewInputTextField'
import { rtoSuiteAddressMinLength, SENSITIVITY_PRESETS, DEFAULT_GIBBERISH_THRESHOLD } from 'src/lib/utils/constants'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shopflo/ui'

const AddressFormSettings = () => {
  const {
    actions: { setAddressConfig },
    state: { addressConfig }
  } = useRtoSuiteContext(context => context)
  const { t } = useLocale()

  const [address1MinimumLength, address2MinimumLength] = [
    addressConfig.addressLine1.minCharacterLimit,
    addressConfig.addressLine2.minCharacterLimit
  ]

  return (
    <div className='flex flex-col gap-1'>
      <h2 className='text-text-sm-medium text-zinc-800'>{t('address_line', { num: 1 })}</h2>
      <InputTextFieldV2
        textFieldId='addressLine1'
        inputType='number'
        label={<h2 className='text-text-sm-medium text-zinc-800 py-2'>{t('minimum_characters')}</h2>}
        bottomDescription={t('minimum_number_of_characters_required')}
        inputValue={address1MinimumLength ? address1MinimumLength : ''}
        handleChange={e => {
          const value = Number(e.target.value)
          setAddressConfig({
            ...addressConfig,
            addressLine1: {
              ...addressConfig.addressLine1,
              minCharacterLimit: value ? value : 0
            }
          })
        }}
        errorStatus={addressConfig.addressLine1.minCharacterLimit < rtoSuiteAddressMinLength}
        errorMessage={t('value_should_be_greater_than_or_equal_to', { value: rtoSuiteAddressMinLength })}
      />

      <hr className='my-4' />

      <h2 className='text-text-sm-medium text-zinc-800'>{t('address_line', { num: 2 })}</h2>

      <div className='flex flex-col'>
        <FloCheckbox
          label={<p className='text-text-sm-medium text-zinc-600'>{t('isRequired_checkbox_description')}</p>}
          value={addressConfig.addressLine2?.isMandatory ?? false}
          setValue={() =>
            setAddressConfig({
              ...addressConfig,
              addressLine2: {
                ...addressConfig.addressLine2,
                isMandatory: !addressConfig.addressLine2.isMandatory
              }
            })
          }
        />
      </div>

      <InputTextFieldV2
        isDisabled={!addressConfig.addressLine2.isMandatory}
        textFieldId='addressLine2'
        inputType='number'
        label={<h2 className='text-text-sm-medium text-carbon py-2'>{t('minimum_characters')}</h2>}
        bottomDescription={t('minimum_number_of_characters_required')}
        inputValue={address2MinimumLength ? address2MinimumLength : ''}
        handleChange={e => {
          const value = Number(e.target.value)
          setAddressConfig({
            ...addressConfig,
            addressLine2: {
              ...addressConfig.addressLine2,
              minCharacterLimit: value ? value : 0
            }
          })
        }}
        errorStatus={
          addressConfig.addressLine2.minCharacterLimit < rtoSuiteAddressMinLength &&
          addressConfig.addressLine2.isMandatory
        }
        errorMessage={t('value_should_be_greater_than_or_equal_to', { value: rtoSuiteAddressMinLength })}
      />

      <hr className='my-4' />

      <h2 className='text-text-sm-medium text-zinc-800'>{t('email')}</h2>

      <div className='flex flex-col'>
        <FloCheckbox
          label={<p className='text-text-sm-medium text-zinc-600'>{t('isRequired_checkbox_description')}</p>}
          value={addressConfig.email?.isMandatory ?? true}
          setValue={() =>
            setAddressConfig({
              ...addressConfig,
              email: {
                ...addressConfig.email,
                isMandatory: !addressConfig.email.isMandatory
              }
            })
          }
        />
      </div>

      <hr className='my-4' />

      <h2 className='text-text-sm-medium text-zinc-800'>{t('gibberish_detection')}</h2>
      <p className='text-text-sm-regular text-zinc-600 mb-2'>{t('gibberish_detection_description')}</p>

      <div className='flex flex-col gap-3'>
        <FloCheckbox
          label={<p className='text-text-sm-medium text-zinc-600'>{t('enable_gibberish_detection')}</p>}
          value={addressConfig.gibberishCheckEnabled}
          setValue={() =>
            setAddressConfig({
              ...addressConfig,
              gibberishCheckEnabled: !addressConfig.gibberishCheckEnabled
            })
          }
        />

        {addressConfig.gibberishCheckEnabled && (
          <>
            <FloCheckbox
              label={<p className='text-text-sm-medium text-zinc-600'>{t('allow_unicode_characters')}</p>}
              value={addressConfig.allowUnicode}
              setValue={() =>
                setAddressConfig({
                  ...addressConfig,
                  allowUnicode: !addressConfig.allowUnicode
                })
              }
            />

            <div className='flex flex-col gap-1'>
              <h2 className='text-text-sm-medium text-zinc-800 py-2'>{t('gibberish_sensitivity')}</h2>
              <Select
                value={String(addressConfig.gibberishThreshold)}
                onValueChange={val => setAddressConfig({ ...addressConfig, gibberishThreshold: Number(val) })}
              >
                <SelectTrigger className='rounded-lg h-9 w-64'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SENSITIVITY_PRESETS.map(preset => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {t(preset.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-text-xs-regular text-zinc-600'>
                {t(
                  (SENSITIVITY_PRESETS.find(p => p.value === String(addressConfig.gibberishThreshold)) ??
                    SENSITIVITY_PRESETS.find(p => p.value === DEFAULT_GIBBERISH_THRESHOLD))?.descriptionKey ?? ''
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AddressFormSettings
